// =============================================================================
// API CLIENT - Клієнт для взаємодії з War Game API
// =============================================================================

import type {
  GameScenario,
  Message,
  InitResponse,
  NextScenarioResponse,
  EndingResponse,
  ErrorResponse,
  GameOverResponse,
  DonationCampaign,
} from '../types';
import { requireEnvUrl } from '../lib/env';

/**
 * URL API сервера
 */
const API_BASE_URL = requireEnvUrl('VITE_API_URL');

type ApiResponse<T extends object> = T | ErrorResponse;

type SSEEvent = {
  event?: string;
  data?: string;
};

function parseSSEEventBlock(block: string): SSEEvent {
  const lines = block
    .split('\n')
    .map((l) => l.replace(/\r$/, ''))
    .filter(Boolean);

  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }

  return {
    event,
    data: dataLines.length ? dataLines.join('\n') : undefined,
  };
}

async function postSSE<T extends object>(
  endpoint: string,
  body: unknown,
  handlers: {
    onDelta?: (textDelta: string) => void;
    signal?: AbortSignal;
  }
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: handlers.signal,
  });

  if (!response.ok) {
    let text = '';
    try {
      text = await response.text();
    } catch {
      // ignore
    }
    throw new Error(text || `API помилка: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Streaming не підтримується в цьому середовищі');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  return new Promise<T>((resolve, reject) => {
    const pump = async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by a blank line
          let separatorIndex: number;
          // eslint-disable-next-line no-cond-assign
          while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
            const block = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            if (!block.trim()) continue;

            const { event, data } = parseSSEEventBlock(block);
            if (!event) continue;

            if (event === 'delta') {
              if (!data) continue;
              try {
                const parsed = JSON.parse(data) as { text?: string };
                if (parsed.text && handlers.onDelta) {
                  handlers.onDelta(parsed.text);
                }
              } catch {
                // ignore malformed delta
              }
              continue;
            }

            if (event === 'gameOver') {
              if (!data) {
                reject(new Error('Порожній результат програшу'));
                return;
              }
              resolve(JSON.parse(data) as T);
              return;
            }

            if (event === 'result') {
              if (!data) {
                reject(new Error('Порожній результат стрімінгу'));
                return;
              }
              resolve(JSON.parse(data) as T);
              return;
            }

            if (event === 'error') {
              if (!data) {
                reject(new Error('Помилка стрімінгу'));
                return;
              }
              try {
                const parsed = JSON.parse(data) as ErrorResponse;
                reject(new Error(parsed.error || 'Помилка стрімінгу'));
              } catch {
                reject(new Error('Помилка стрімінгу'));
              }
              return;
            }
          }
        }

        reject(new Error('Стрімінг завершився без результату'));
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Помилка стрімінгу'));
      }
    };

    pump();
  });
}

/**
 * Базовий fetch з обробкою помилок
 */
async function apiFetch<T extends object>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    const data = (await response.json()) as ApiResponse<T>;

    if (!response.ok || ('success' in data && data.success === false)) {
      const errorData = data as ErrorResponse;
      throw new Error(errorData.error || `API помилка: ${response.status}`);
    }

    return data as T;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Невідома помилка при запиті до API');
  }
}

/**
 * Отримати початковий сценарій та конфігурацію гри
 */
export async function initGame(): Promise<InitResponse> {
  return apiFetch<InitResponse>('/api/init');
}

/**
 * Стрімінг наслідків + повернення фінального сценарію
 */
export async function getNextScenarioStream(
  conversationHistory: Message[],
  playerChoice: string,
  currentDecision: number,
  model: string | undefined,
  currentStats: {
    militaryStrength: number;
    economy: number;
    resilience: number;
    westernSupport: number;
    trust: number;
  },
  handlers: {
    onDelta?: (textDelta: string) => void;
    signal?: AbortSignal;
  }
): Promise<
  | { type: 'scenario'; scenario: GameScenario; consequences: string }
  | { type: 'gameOver'; response: GameOverResponse }
> {
  const response = await postSSE<NextScenarioResponse | GameOverResponse>(
    '/api/scenario/stream',
    {
      conversationHistory,
      playerChoice,
      currentDecision,
      model,
      currentStats,
    },
    handlers
  );

  // Check if this is a game over response
  if ('gameOver' in response && response.gameOver) {
    return {
      type: 'gameOver',
      response: response as GameOverResponse,
    };
  }

  const scenarioResponse = response as NextScenarioResponse;
  return {
    type: 'scenario',
    scenario: scenarioResponse.scenario,
    consequences: scenarioResponse.consequences,
  };
}

/**
 * Стрімінг фінального підсумку
 */
export async function generateEndingStream(
  conversationHistory: Message[],
  finalStats: {
    militaryStrength: number;
    economy: number;
    resilience: number;
    westernSupport: number;
    trust: number;
  },
  model: string | undefined,
  handlers: {
    onDelta?: (textDelta: string) => void;
    signal?: AbortSignal;
  }
): Promise<string> {
  const response = await postSSE<EndingResponse>(
    '/api/ending/stream',
    {
      conversationHistory,
      finalStats,
      model,
    },
    handlers
  );

  return response.summary;
}

/**
 * Створити нову кампанію збору (повертає ID та slug)
 */
export async function createCampaign(
  campaign: Omit<DonationCampaign, 'id'>
): Promise<{ success: boolean; id: string; slug?: string }> {
  return apiFetch<{ success: boolean; id: string; slug?: string }>('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(campaign),
  });
}

/**
 * Перевірити доступність slug
 */
export async function checkSlugAvailable(
  slug: string
): Promise<{ available: boolean; reason?: string }> {
  try {
    const response = await apiFetch<{
      success: boolean;
      available: boolean;
      reason?: string;
    }>(`/api/campaigns/check/${encodeURIComponent(slug)}`);
    return { available: response.available, reason: response.reason };
  } catch {
    return { available: false, reason: 'error' };
  }
}

/**
 * Отримати кампанію за slug
 */
export async function getCampaignBySlug(
  slug: string
): Promise<DonationCampaign | null> {
  try {
    const response = await apiFetch<{
      success: boolean;
      campaign: DonationCampaign;
    }>(`/api/campaigns/slug/${encodeURIComponent(slug)}`);
    return response.campaign;
  } catch (error) {
    if (error instanceof Error && error.message.includes('NOT_FOUND')) {
      return null;
    }
    throw error;
  }
}

/**
 * Отримати кампанію за ID
 */
export async function getCampaign(
  id: string
): Promise<DonationCampaign | null> {
  try {
    const response = await apiFetch<{
      success: boolean;
      campaign: DonationCampaign;
    }>(`/api/campaigns/${id}`);
    return response.campaign;
  } catch (error) {
    if (error instanceof Error && error.message.includes('NOT_FOUND')) {
      return null;
    }
    throw error;
  }
}

/**
 * Fallback підсумок при помилці API
 */
export function getFallbackEnding(averageScore: number): string {
  if (averageScore > 70) {
    return 'Ваше керівництво забезпечило Україні сильні позиції. Армія боєздатна, народ згуртований, світ на нашому боці. Перемога - питання часу.';
  } else if (averageScore > 50) {
    return 'Попереду ще багато випробувань, але Україна тримається. Кожен день спротиву - це крок до перемоги.';
  } else {
    return 'Ситуація критична, але український дух незламний. Історія ще не написана, і боротьба продовжується.';
  }
}

/**
 * Fallback сценарій при помилці API
 */
export const FALLBACK_SCENARIO: GameScenario = {
  decisionNumber: 0,
  date: 'Невідомо',
  location: 'Командний центр',
  urgency: 'HIGH',
  briefing: `⚠️ ТЕХНІЧНА ПРОБЛЕМА

Зв'язок з командним центром тимчасово втрачено. Системи відновлюються...

Спробуйте повторити запит або оновіть сторінку.`,
  options: [
    {
      id: 'retry',
      text: "Спробувати відновити зв'язок",
      description: 'Повторний запит до системи',
      impact: {
        militaryStrength: 0,
        economy: 0,
        resilience: 0,
        westernSupport: 0,
        trust: 0,
      },
    },
  ],
};
